import * as React from "react";
import {
    DateFormats,
    IAvailabilitySchedule,
    IClinician,
    RecurrenceRule,
    IIntakeAvailability,
} from "intake_shared/src/types";
import moment from "moment";
import {
    getDayTimeFromScheduleDate,
    getNextXWeeksMoment,
    getNumberOfWeeks,
} from "../../../../utils/utils";
import { Badge } from "../../bits/Badge/Badge";
import { CacheContext } from "../../../../contexts/CacheContext";
import { AclContext } from "../../../../contexts/AclContext";
import ClientDisplayDialog, {
    defaultClient,
} from "../../clients/ClientDisplayDialog/ClientDisplayDialog";
import { CalendarId, RECURRENCE_COLORS } from "../../../../constants";
import { Button } from "../../bits/Button/Button";
import EditIcon from "../../icons/Edit";
import { AlertDialog } from "../../bits/AlertDialog/AlertDialog";
import styles from "./ScheduleBox.module.scss";
import { getIntakeStatusBadgeProps } from "../../../v2utils";
import { cn } from "../../../helpers";
import TrashIcon from "../../icons/Trash";
import CancelIcon from "../../icons/Cancel";
import store from "../../../../store";
import DeleteScheduleDialog from "../DeleteScheduleDialog/DeleteScheduleDialog";

const checkOneOffAppointmentDate = (schedule: IAvailabilitySchedule) =>
    moment(
        (schedule.starting_date || schedule.start) &&
            (schedule.end || schedule.ending_date)
    );

function getDateDescLabel(schedule: IAvailabilitySchedule, day: number) {
    const startingMoment = moment(schedule.starting_date || schedule.start);
    const isPassed = startingMoment.isBefore(moment(), "day");
    const oneOffAppointmentDate = checkOneOffAppointmentDate(schedule);
    const isBeforeCurrentDate = oneOffAppointmentDate.isBefore(moment());
    let numberOfWeeks = getNumberOfWeeks(schedule.recurrenceRule);
    let dateDescLabel = "";
    if (schedule.recurrenceRule === RecurrenceRule.ONE_OFF) {
        dateDescLabel = `${dateDescLabel} Happening on ${startingMoment.format(
            DateFormats.DAY_MONTH_FORMAT
        )}`;
    } else {
        if (!isPassed) {
            dateDescLabel = `${dateDescLabel} Starting ${startingMoment.format(
                DateFormats.DAY_MONTH_FORMAT
            )}`;
        } else {
            dateDescLabel = `${dateDescLabel} Next on ${getNextXWeeksMoment(
                startingMoment,
                numberOfWeeks,
                day
            ).format(DateFormats.DAY_MONTH_FORMAT)}`;
        }
    }
    if (schedule.recurrenceRule === RecurrenceRule.ONE_OFF && isBeforeCurrentDate) {
        return (
            <span>
                <Badge
                    tone="danger"
                    content="Session Expired,Edit or Cancel"
                    className={styles.expiredSessionBadge}
                />{" "}
            </span>
        );
    }
    if (schedule.ending_date) {
        dateDescLabel = `${dateDescLabel}, Ends ${moment(
            schedule.ending_date
        ).format(DateFormats.DAY_MONTH_FORMAT)}`;
    }
    return dateDescLabel;
}

interface ScheduleBoxProps {
    schedule: IAvailabilitySchedule;
    onRemoveSchedule: (schedule: IAvailabilitySchedule) => void;
    setScheduleToEdit: React.Dispatch<
        React.SetStateAction<IAvailabilitySchedule | null>
    >;
    onSelectSchedule?: (
        schedule: IAvailabilitySchedule
    ) => Promise<IAvailabilitySchedule[]>;
    format?: string;
    onUpdateCaseLoad: (() => Promise<void> | undefined) | undefined;
    loggedInClinician: IClinician | undefined;
    onRemoveOfficeWindow?: (schedule: IAvailabilitySchedule) => void;
    officeSchedulesInReview?: IAvailabilitySchedule[];
    setRefreshKey?: (value: React.SetStateAction<number>) => void;
    // Intake session handlers
    onEditIntakeSession?: (schedule: IAvailabilitySchedule) => void;
    intakeSessionAvailability?: IIntakeAvailability[];
}

export default function ScheduleBox({
    schedule,
    onRemoveSchedule,
    onRemoveOfficeWindow,
    setScheduleToEdit,
    onSelectSchedule,
    format = "h:mma",
    onUpdateCaseLoad,
    loggedInClinician,
    officeSchedulesInReview,
    setRefreshKey,
    onEditIntakeSession,
    intakeSessionAvailability = [],
}: ScheduleBoxProps) {
    const startStr = getDayTimeFromScheduleDate(schedule.start, format);
    const endStr = getDayTimeFromScheduleDate(schedule.end, format);
    const dateDescLabel = getDateDescLabel(schedule, moment(schedule.start).day());

    const olc = React.useContext(CacheContext);
    const oneOffAppointmentDate = checkOneOffAppointmentDate(schedule);
    const isBeforeCurrentDate = oneOffAppointmentDate.isBefore(moment());
    const userAcl = React.useContext(AclContext)?.authUser?.acl;
    const [clientForm, setClientForm] = React.useState({
        show: false,
        allowEdit: true,
        client: defaultClient,
        allowChangeStatus: false,
        bookingId: "",
    });

    const [showDeleteDialog, setShowDeleteDialog] = React.useState(false);
    const [showDeleteScheduleDialog, setShowDeleteScheduleDialog] =
        React.useState(false);

    const getClientSessionToCancel = async (client_id: string) => {
        const client = await store.getClientById(client_id);
        if (client) {
            setClientForm({
                client,
                allowEdit: false,
                show: true,
                allowChangeStatus: false,
                bookingId: schedule.id as string,
            });
        }
    };

    const handleDeleteClick = async () => {
        if (
            schedule.action === "clinician_deleting_office_window" &&
            !userAcl?.super_admin &&
            schedule.location !== "Remote"
        ) {
            return setShowDeleteScheduleDialog(true);
        } else {
            setShowDeleteDialog(true);
        }
    };

    const handleDeleteConfirm = async () => {
        // Check if it's an intake session - route to parent handler
        if (
            (schedule as any).isIntakeSession ||
            schedule.calendarId === CalendarId.INTAKE_SESSION_AVAILABILITY
        ) {
            onRemoveSchedule(schedule);
            setShowDeleteDialog(false);
            return;
        }

        if (
            schedule.action === "clinician_deleting_availability" ||
            schedule.action === "admin_deleting_clinician_office_window"
        ) {
            onRemoveSchedule(schedule);
        } else if (
            (userAcl?.super_admin &&
                schedule.action === "clinician_deleting_office_window") ||
            (schedule.action === "clinician_deleting_office_window" &&
                schedule.location === "Remote")
        ) {
            onRemoveOfficeWindow && onRemoveOfficeWindow(schedule);
        }
        setShowDeleteDialog(false);
    };

    const getDeleteDialogDescription = () => {
        if (
            ((schedule as any).isIntakeSession &&
                (schedule as any).isIntakeSessionBooked) ||
            (schedule.calendarId === CalendarId.INTAKE_SESSION_AVAILABILITY &&
                (schedule as any).isIntakeSessionBooked)
        ) {
            return (
                <>
                    Are you sure you want to cancel this session? If you only need to
                    change the schedule, click the <EditIcon /> icon outside of this
                    modal to reschedule instead.
                    <strong>
                        <p style={{ marginTop: 20 }}>
                            {schedule.title}: {startStr} - {endStr}
                        </p>
                    </strong>
                </>
            );
        }

        const hasConflicts =
            (schedule.action === "admin_deleting_clinician_office_window" &&
                userAcl?.super_admin) ||
            (userAcl?.super_admin &&
                schedule.action === "clinician_deleting_office_window") ||
            (schedule.action === "clinician_deleting_office_window" &&
                schedule.location === "Remote");

        if (hasConflicts && onSelectSchedule) {
            return (
                <div>
                    We found availability slots that uses this office window and we
                    are going to delete them.
                    <br />
                    Are you sure you want to delete this schedule?
                    <strong>
                        <p>
                            {schedule.title}: {startStr} - {endStr}
                        </p>
                    </strong>
                </div>
            );
        }

        return (
            <>
                Are you sure you want to delete this schedule?
                <strong>
                    <p>
                        {schedule.title}: {startStr} - {endStr}
                    </p>
                </strong>
            </>
        );
    };

    const renderScheduleActions = () => {
        const isPendingOfficeWindowSchedule =
            officeSchedulesInReview?.some(
                (reviewSchedule) => reviewSchedule.id === schedule.id
            ) && schedule.calendarId === CalendarId.OFFICE;

        const shouldShowActions =
            !isPendingOfficeWindowSchedule &&
            (!schedule.isReadOnly ||
                schedule.canEdit ||
                (schedule.recurrenceRule === RecurrenceRule.ONE_OFF &&
                    isBeforeCurrentDate &&
                    schedule.calendarId === CalendarId.CONFIRMED_BOOKINGS));

        return (
            <>
                {shouldShowActions && (
                    <div className={styles.scheduleActions}>
                        {!schedule.isReadOnly &&
                            !officeSchedulesInReview?.some(
                                (reviewSchedule) => reviewSchedule.id === schedule.id
                            ) && (
                                <Button
                                    onClick={handleDeleteClick}
                                    variant="normal"
                                    className={styles.scheduleActionButton}
                                >
                                    {(schedule as any).isIntakeSessionBooked ? (
                                        <CancelIcon />
                                    ) : (
                                        <TrashIcon />
                                    )}
                                </Button>
                            )}
                        {schedule.recurrenceRule === RecurrenceRule.ONE_OFF &&
                            isBeforeCurrentDate &&
                            schedule.calendarId ===
                                CalendarId.CONFIRMED_BOOKINGS && (
                                <Button
                                    onClick={() => {
                                        getClientSessionToCancel(
                                            schedule.client_id as string
                                        );
                                    }}
                                    variant="normal"
                                    className={styles.scheduleActionButton}
                                >
                                    <TrashIcon />
                                </Button>
                            )}
                        {schedule.canEdit && (
                            <Button
                                onClick={() => {
                                    // Check if it's an intake session - call parent handler
                                    if (
                                        (schedule as any).isIntakeSession ||
                                        schedule.calendarId ===
                                            CalendarId.INTAKE_SESSION_AVAILABILITY
                                    ) {
                                        if (onEditIntakeSession) {
                                            onEditIntakeSession(schedule);
                                        }
                                    } else {
                                        // For regular schedules, use EditScheduleDialog
                                        setScheduleToEdit(schedule);
                                    }
                                }}
                                variant="normal"
                                className={styles.scheduleActionButton}
                            >
                                <EditIcon stroke="var(--color-gray)" />
                            </Button>
                        )}
                    </div>
                )}
                {officeSchedulesInReview?.some(
                    (reviewSchedule) => reviewSchedule.id === schedule.id
                ) && (
                    <span>
                        <Badge tone="warning" content="In review" />
                    </span>
                )}
            </>
        );
    };

    if (schedule.isVisible) {
        const calendarClass = schedule.calendarId ? styles[schedule.calendarId] : "";
        const className = cn(styles.scheduleBox, calendarClass);

        return (
            <>
                <div key={schedule.id} className={className}>
                    <div className={styles.scheduleContent}>
                        <ClientDisplayDialog
                            clientDialogOptions={{
                                ...clientForm,
                                onHide: () => {
                                    setClientForm({
                                        ...clientForm,
                                        show: false,
                                    });
                                },
                                onSaveClient: async (c) => {
                                    setClientForm({
                                        ...clientForm,
                                        show: false,
                                    });
                                    if (onUpdateCaseLoad) {
                                        await onUpdateCaseLoad();
                                    }
                                },
                                bookingId: clientForm.bookingId,
                                expectedCaller: userAcl?.super_admin
                                    ? "admin"
                                    : "clinician",
                            }}
                        />
                        <p className={styles.scheduleTitle}>{schedule.title}</p>
                        {`${startStr} - ${endStr}`}

                        {schedule.availableService ? (
                            <span>
                                {
                                    (olc?.requestedServices || {})[
                                        schedule.availableService
                                    ]
                                }
                            </span>
                        ) : (
                            <></>
                        )}
                        <span>{dateDescLabel}</span>

                        <span>
                            <Badge
                                content={schedule.recurrenceRule}
                                bgColor={
                                    RECURRENCE_COLORS[
                                        (schedule.recurrenceRule
                                            ?.trim()
                                            .toLowerCase() ||
                                            RecurrenceRule.WEEKLY) as string
                                    ]
                                }
                                textColor="var(--color-white)"
                            />
                        </span>
                        {(schedule as any).isIntakeSession && (
                            <span>
                                {(() => {
                                    const { bgColor, label } =
                                        getIntakeStatusBadgeProps(
                                            (schedule as any).status
                                        );
                                    return (
                                        <Badge
                                            content={label}
                                            bgColor={bgColor}
                                            textColor="var(--color-white)"
                                            className={styles.intakeStatusBadge}
                                        />
                                    );
                                })()}
                            </span>
                        )}
                    </div>
                    {renderScheduleActions()}
                </div>
                <DeleteScheduleDialog
                    schedule={schedule}
                    setScheduleToEdit={setScheduleToEdit}
                    loggedInClinician={loggedInClinician}
                    setShowDeleteScheduleDialog={setShowDeleteScheduleDialog}
                    showDeleteScheduleDialog={showDeleteScheduleDialog}
                    setRefreshKey={setRefreshKey}
                />

                <AlertDialog
                    open={showDeleteDialog}
                    onClose={() => setShowDeleteDialog(false)}
                    title={
                        (schedule as any).isIntakeSessionBooked
                            ? "Cancel Session"
                            : "Delete Schedule"
                    }
                    description={getDeleteDialogDescription()}
                    saveText={
                        (schedule as any).isIntakeSessionBooked ? "Cancel" : "Delete"
                    }
                    cancelText="Cancel"
                    onSaveClick={handleDeleteConfirm}
                    onCancelClick={() => setShowDeleteDialog(false)}
                    isDeleting={true}
                />
            </>
        );
    }

    return <></>;
}
